import { LocalStorageKey, SessionStorageKey } from 'src/types'

/**
 * Seeds sessionStorage only so App.tsx can send the active-org header without
 * treating a membership default as an explicit cross-tab user choice.
 */
const seedSessionActiveOrgId = (orgId: number): void => {
  sessionStorage.setItem(SessionStorageKey.ActiveOrgId, String(orgId))
}

/**
 * Writes the active org to both sessionStorage (per-tab) and localStorage
 * (cross-tab default) so the two stores stay in sync. Called by
 * `switchActiveOrg` and the path-sync effect in `AppStateContextLayout`.
 */
export const persistActiveOrgId = (orgId: number): void => {
  seedSessionActiveOrgId(orgId)
  localStorage.setItem(LocalStorageKey.LastSelectedOrgId, String(orgId))
}

/**
 * Reads the active org ID from browser storage, validated against the user's
 * membership org IDs. Returns `undefined` if no valid org is found in storage.
 *
 * Resolution order:
 * 1. **sessionStorage** (per-tab) -- each browser tab tracks its own active org,
 *    so a user can have different orgs open in different tabs simultaneously.
 * 2. **localStorage** (cross-tab) -- stores the last org the user explicitly
 *    chose (org switcher) or landed on via deep link. Used as the default when
 *    opening a new tab that has no
 *    sessionStorage value yet. When this fallback is used, sessionStorage is
 *    seeded so the Apollo link in `App.tsx` picks up the header immediately.
 *
 * This is a plain function (not a hook) so that AppStateContextLayout can call
 * it to resolve the org ID before AppStateContext is available.
 */
export const getActiveOrgIdFromStorage = (membershipOrgIds: Set<number>): number | undefined => {
  const sessionVal = sessionStorage.getItem(SessionStorageKey.ActiveOrgId)
  const sessionOrgId = sessionVal ? Number(sessionVal) : undefined
  if (sessionOrgId && membershipOrgIds.has(sessionOrgId)) {
    return sessionOrgId
  }

  const localVal = localStorage.getItem(LocalStorageKey.LastSelectedOrgId)
  const localOrgId = localVal ? Number(localVal) : undefined
  if (localOrgId && membershipOrgIds.has(localOrgId)) {
    seedSessionActiveOrgId(localOrgId)
    return localOrgId
  }

  return undefined
}

/**
 * Full resolution chain for AppStateContextLayout:
 * sessionStorage -> localStorage -> first membership, seeding sessionStorage
 * whenever storage was empty so the Apollo header is present on first request.
 */
export const resolveActiveOrgId = (
  membershipOrgIds: Set<number>,
  firstMembershipOrgId: number | undefined
): number | undefined => {
  const fromStorage = getActiveOrgIdFromStorage(membershipOrgIds)
  if (fromStorage !== undefined) {
    return fromStorage
  }

  if (firstMembershipOrgId !== undefined && membershipOrgIds.has(firstMembershipOrgId)) {
    seedSessionActiveOrgId(firstMembershipOrgId)
    return firstMembershipOrgId
  }

  return undefined
}

/**
 * Writes the selected org to both stores, then triggers a full page reload
 * to the homepage.
 */
export const switchActiveOrg = (orgId: number): void => {
  persistActiveOrgId(orgId)
  window.location.assign('/')
}
